/**
 * Shared glance chips for thread list/search cards: objects, files, updates digest.
 */
import { FileText } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { ThreadFilePreview, ThreadObjectPreview } from '@/hooks/use-threads'

export function ThreadUpdatesDigest({
  updates,
  className,
}: {
  updates?: string[] | null
  className?: string
}) {
  if (!updates?.length) return null
  return (
    <ul className={cn('space-y-0.5 text-xs text-muted-foreground', className)}>
      {updates.slice(0, 4).map((line, i) => (
        <li key={i} className="truncate">
          · {line}
        </li>
      ))}
    </ul>
  )
}

export function ThreadObjectChips({
  objects,
  activity,
  objectCount,
  className,
}: {
  objects?: ThreadObjectPreview[] | null
  activity?: Record<string, number> | null
  objectCount?: number
  className?: string
}) {
  if (objects && objects.length > 0) {
    const overflow =
      typeof objectCount === 'number' && objectCount > objects.length
        ? objectCount - objects.length
        : 0
    return (
      <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
        {objects.map((obj) => (
          <Badge
            key={`${obj.type}-${obj.id}`}
            variant="outline"
            className="max-w-full truncate px-1.5 py-0.5 text-xs font-medium"
            title={`${obj.action} ${obj.type}`}
          >
            <span className="truncate text-foreground">
              {obj.name || obj.type}
            </span>
          </Badge>
        ))}
        {overflow > 0 ? (
          <span className="text-xs text-muted-foreground">+{overflow} more</span>
        ) : null}
      </div>
    )
  }

  if (activity && Object.keys(activity).length > 0) {
    const entries = Object.entries(activity).sort((a, b) => b[1] - a[1]).slice(0, 3)
    return (
      <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
        {entries.map(([type, count]) => (
          <Badge
            key={type}
            variant="outline"
            className="px-1.5 py-0.5 text-xs font-medium tabular-nums"
          >
            <span className="text-muted-foreground">{type}</span>
            <span className="ml-1 text-foreground">{count}</span>
          </Badge>
        ))}
      </div>
    )
  }

  return null
}

export function ThreadFileChips({
  files,
  fileCount,
  className,
}: {
  files?: ThreadFilePreview[] | null
  fileCount?: number
  className?: string
}) {
  const count = fileCount ?? files?.length ?? 0
  if (count <= 0) return null

  const visible = files?.slice(0, 3) ?? []
  const overflow = count > visible.length ? count - visible.length : 0

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {visible.length > 0 ? (
        visible.map((f) => (
          <Badge
            key={f.id}
            variant="secondary"
            className="max-w-[12rem] gap-1 truncate px-1.5 py-0.5 text-xs font-normal"
            title={f.original_name}
          >
            <FileText className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate">{f.original_name}</span>
          </Badge>
        ))
      ) : (
        <Badge variant="secondary" className="gap-1 px-1.5 py-0.5 text-xs font-normal">
          <FileText className="h-3 w-3" aria-hidden />
          <span className="tabular-nums">{count}</span>
          {count === 1 ? ' file' : ' files'}
        </Badge>
      )}
      {overflow > 0 ? (
        <span className="text-xs text-muted-foreground">+{overflow} more</span>
      ) : null}
    </div>
  )
}

export function ThreadLatestUpdateLine({
  latest,
  className,
}: {
  latest?: {
    object_type: string
    object_name?: string
    action: string
    text?: string
  } | null
  className?: string
}) {
  if (!latest) return null
  const label = latest.object_name || latest.object_type
  const line = latest.text?.trim() || `${latest.action} ${label}`
  return (
    <p className={cn('truncate text-xs text-muted-foreground', className)} title={line}>
      Latest: {line}
    </p>
  )
}
